/**
 * Audio without files: SFX and BGM are synthesised at runtime with WebAudio.
 * Howler is loaded for compatibility but BGM defaults to procedural chiptune
 * (10 unique loops, one per world). No network assets needed.
 *
 * Public API:
 *   AudioManager.init()                – call once after first user gesture
 *   AudioManager.sfx(name)             – fire-and-forget sound effect
 *   AudioManager.startBgmForWorld(w)   – begin chiptune loop for world 1..10
 *   AudioManager.stopBgm()             – stop background loop
 *   AudioManager.toggleMute()          – global mute toggle (persisted)
 *   AudioManager.isMuted               – current state
 */


type SfxName =
  | 'plant'
  | 'explode'
  | 'block-break'
  | 'powerup'
  | 'hit'
  | 'die'
  | 'enemy-die'
  | 'boss-hit'
  | 'boss-die'
  | 'menu-move'
  | 'menu-select'
  | 'level-win'
  | 'continue';

const MUTE_KEY = 'bombastik:audio:muted:v1';

class AudioManagerImpl {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private bgmTimer: number | null = null;
  private bgmCurrentWorld = 0;
  isMuted = false;

  init() {
    if (this.ctx) {
      // Resume suspended context (Chrome blocks autoplay until gesture)
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {/* ignore */});
      return;
    }
    try {
      this.isMuted = localStorage.getItem(MUTE_KEY) === '1';
      const savedVol = localStorage.getItem('bombastik:audio:volume:v1');
      if (savedVol) this.volume = Math.max(0, Math.min(1, parseFloat(savedVol)));
    } catch { /* ignore */ }
    try {
      const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.isMuted ? 0 : 0.5 * this.volume;
      this.master.connect(this.ctx.destination);
      // Immediately resume — needed on browsers that create the context suspended
      this.ctx.resume().catch(() => {/* ignore */});
    } catch (e) {
      console.warn('AudioManager: WebAudio unavailable', e);
    }
  }

  volume = 1.0;

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.master) this.master.gain.value = this.isMuted ? 0 : 0.5 * this.volume;
    if (this.bgmGain) this.bgmGain.gain.value = this.isMuted ? 0 : 0.18 * this.volume;
    try { localStorage.setItem(MUTE_KEY, this.isMuted ? '1' : '0'); } catch { /* ignore */ }
    return this.isMuted;
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    try { localStorage.setItem('bombastik:audio:volume:v1', String(this.volume)); } catch { /* ignore */ }
    if (this.isMuted) return;
    if (this.master) this.master.gain.value = 0.5 * this.volume;
    if (this.bgmGain) this.bgmGain.gain.value = 0.18 * this.volume;
  }

  sfx(name: SfxName) {
    if (!this.ctx) this.init();
    if (this.ctx?.state === 'suspended') this.ctx.resume().catch(() => {/* ignore */});
    if (this.isMuted || !this.ctx || !this.master) return;
    switch (name) {
      case 'plant':       this.tone({ freq: 220, dur: 0.08, type: 'square', glide: -40, gain: 0.25 }); break;
      case 'explode':     this.boom(); break;
      case 'block-break': this.noise(0.10, 0.25, 0.2); break;
      case 'powerup':     this.arpeggio([523, 659, 784], 0.06, 'square', 0.2); break;
      case 'hit':         this.tone({ freq: 180, dur: 0.18, type: 'sawtooth', glide: -60, gain: 0.3 }); break;
      case 'die':         this.arpeggio([400, 300, 220, 160, 110], 0.10, 'sawtooth', 0.3); break;
      case 'enemy-die':   this.tone({ freq: 600, dur: 0.12, type: 'triangle', glide: -300, gain: 0.2 }); break;
      case 'boss-hit':    this.tone({ freq: 90, dur: 0.18, type: 'sawtooth', glide: -10, gain: 0.4 }); break;
      case 'boss-die':    this.arpeggio([220, 280, 350, 440, 550, 660], 0.12, 'square', 0.35); break;
      case 'menu-move':   this.tone({ freq: 660, dur: 0.04, type: 'square', glide: 0, gain: 0.15 }); break;
      case 'menu-select': this.arpeggio([523, 784], 0.06, 'square', 0.25); break;
      case 'level-win':   this.arpeggio([523, 659, 784, 1046], 0.12, 'square', 0.3); break;
      case 'continue':    this.tone({ freq: 440, dur: 0.10, type: 'triangle', glide: 200, gain: 0.25 }); break;
    }
  }

  // ---- tone helpers ----

  private tone(opts: { freq: number; dur: number; type: OscillatorType; glide: number; gain: number }) {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, ctx.currentTime);
    if (opts.glide !== 0) osc.frequency.linearRampToValueAtTime(opts.freq + opts.glide, ctx.currentTime + opts.dur);
    g.gain.setValueAtTime(opts.gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + opts.dur);
    osc.connect(g).connect(this.master!);
    osc.start();
    osc.stop(ctx.currentTime + opts.dur + 0.02);
  }

  private arpeggio(freqs: number[], step: number, type: OscillatorType, gain: number) {
    const ctx = this.ctx!;
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t0 = ctx.currentTime + i * step;
      osc.type = type;
      osc.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + step);
      osc.connect(g).connect(this.master!);
      osc.start(t0);
      osc.stop(t0 + step + 0.02);
    });
  }

  private boom() {
    const ctx = this.ctx!;
    // Low-frequency thump + noise burst
    const osc = ctx.createOscillator();
    const og = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(35, ctx.currentTime + 0.35);
    og.gain.setValueAtTime(0.45, ctx.currentTime);
    og.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.connect(og).connect(this.master!);
    osc.start();
    osc.stop(ctx.currentTime + 0.42);
    this.noise(0.30, 0.35, 0.4);
  }

  private noise(dur: number, gain: number, decay: number) {
    const ctx = this.ctx!;
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decay);
    src.connect(g).connect(this.master!);
    src.start();
  }

  stopBgm() {
    this.stopProceduralBgm();
  }

  // ---- Procedural per-world chiptune BGM ----

  /** Begin (or switch to) the procedural chiptune loop for a given world. */
  startBgmForWorld(world: number) {
    if (!this.ctx) this.init();
    if (this.ctx?.state === 'suspended') this.ctx.resume().catch(() => {/* ignore */});
    if (!this.ctx || !this.master) return;
    const w = Math.max(1, Math.min(10, Math.floor(world)));
    if (this.bgmCurrentWorld === w && this.bgmTimer !== null) return;
    this.stopProceduralBgm();
    this.bgmCurrentWorld = w;
    this.bgmGain = this.ctx.createGain();
    this.bgmGain.gain.value = this.isMuted ? 0 : 0.18;
    this.bgmGain.connect(this.master);
    this.scheduleWorldLoop(w);
  }

  private stopProceduralBgm() {
    if (this.bgmTimer !== null) {
      clearInterval(this.bgmTimer);
      this.bgmTimer = null;
    }
    if (this.bgmGain) {
      try { this.bgmGain.disconnect(); } catch { /* ignore */ }
      this.bgmGain = null;
    }
    this.bgmCurrentWorld = 0;
  }

  /**
   * Each world gets a distinct 8-bar chiptune loop driven by a deterministic
   * recipe: root note, scale, BPM, lead waveform, bass pattern. A single beat
   * timer schedules every note; the loop seamlessly restarts when it ends.
   */
  private scheduleWorldLoop(world: number) {
    if (!this.ctx || !this.bgmGain) return;
    const recipe = this.worldRecipe(world);
    const beatSec = 60 / recipe.bpm / 2; // 8th-note grid
    const stepsPerBar = 8;
    const bars = 4;
    const totalSteps = stepsPerBar * bars;
    let step = 0;

    const tick = () => {
      if (!this.ctx || !this.bgmGain) return;
      const now = this.ctx.currentTime;
      const tInBar = step % stepsPerBar;
      const barIdx = Math.floor(step / stepsPerBar) % bars;
      // Lead melody: pick from scale based on a per-world pattern
      const leadNote = recipe.melody[barIdx * stepsPerBar + tInBar];
      if (leadNote >= 0) {
        const f = this.midiToFreq(recipe.root + recipe.scale[leadNote % recipe.scale.length] + 12 * Math.floor(leadNote / recipe.scale.length));
        this.bgmTone(f, beatSec * 0.9, recipe.leadWave, 0.45, now);
      }
      // Bass: on beats 0,4 of each bar
      if (tInBar % 4 === 0) {
        const bassMidi = recipe.root - 12 + recipe.bass[barIdx % recipe.bass.length];
        this.bgmTone(this.midiToFreq(bassMidi), beatSec * 1.8, 'triangle', 0.55, now);
      }
      // Hat: every other 8th
      if (tInBar % 2 === 1) {
        this.bgmHat(beatSec * 0.4, now);
      }
      step = (step + 1) % totalSteps;
    };

    // Use setInterval for steady tempo (drift is fine for 8-bit loops).
    this.bgmTimer = window.setInterval(tick, beatSec * 1000);
    tick();
  }

  private bgmTone(freq: number, dur: number, type: OscillatorType, gain: number, when: number) {
    if (!this.ctx || !this.bgmGain) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(gain, when + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, when + dur);
    osc.connect(g).connect(this.bgmGain);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  private bgmHat(dur: number, when: number) {
    if (!this.ctx || !this.bgmGain) return;
    const bufferSize = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.12, when);
    g.gain.exponentialRampToValueAtTime(0.001, when + dur);
    src.connect(g).connect(this.bgmGain);
    src.start(when);
  }

  private midiToFreq(midi: number): number {
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  /**
   * Per-world chiptune recipes. Each world has a distinct root, scale, tempo,
   * waveform and melodic pattern so the player audibly "travels" between
   * biomes. Indexes in `melody` are scale-degrees (-1 = rest), 32 entries
   * (4 bars × 8 eighth-notes).
   */
  private worldRecipe(world: number) {
    type Recipe = {
      root: number; scale: number[]; bpm: number;
      leadWave: OscillatorType; melody: number[]; bass: number[];
    };
    const MAJOR = [0, 2, 4, 5, 7, 9, 11];
    const MINOR = [0, 2, 3, 5, 7, 8, 10];
    const PENTA = [0, 2, 4, 7, 9];
    const MIXO  = [0, 2, 4, 5, 7, 9, 10];
    const DORIAN = [0, 2, 3, 5, 7, 9, 10];
    const HARMINOR = [0, 2, 3, 5, 7, 8, 11];

    const R: Record<number, Recipe> = {
      1: { root: 60, scale: MAJOR,    bpm: 128, leadWave: 'square',   melody: [0,2,4,2, 5,4,2,0, 4,5,7,5, 4,2,0,-1, 0,2,4,2, 5,4,2,4, 7,5,4,2, 0,-1,0,-1], bass: [0,4,5,7] },
      2: { root: 62, scale: DORIAN,   bpm: 132, leadWave: 'square',   melody: [0,3,5,3, 7,5,3,0, 5,3,5,7, 8,7,5,3, 0,3,5,7, 8,7,5,3, 7,5,3,0, 0,-1,0,-1], bass: [0,3,5,0] },
      3: { root: 64, scale: PENTA,    bpm: 140, leadWave: 'sawtooth', melody: [0,2,3,2, 4,3,2,0, 3,2,4,3, 2,0,-1,0, 4,3,2,0, 2,3,4,2, 0,-1,2,3, 0,-1,0,-1], bass: [0,3,4,0] },
      4: { root: 57, scale: MINOR,    bpm: 116, leadWave: 'triangle', melody: [0,3,5,3, 7,5,3,2, 0,2,3,5, 7,5,3,0, 0,2,3,5, 7,8,7,5, 3,2,0,2, 3,-1,0,-1], bass: [0,5,3,7] },
      5: { root: 67, scale: MIXO,     bpm: 144, leadWave: 'square',   melody: [0,2,4,2, 4,5,4,2, 0,2,4,5, 6,5,4,2, 0,4,6,5, 4,2,0,2, 4,5,6,4, 2,0,-1,-1], bass: [0,4,2,5] },
      6: { root: 59, scale: HARMINOR, bpm: 124, leadWave: 'square',   melody: [0,2,3,5, 7,8,7,5, 3,2,0,2, 3,2,0,-1, 5,7,8,7, 5,3,2,3, 5,7,5,3, 0,-1,0,-1], bass: [0,5,3,4] },
      7: { root: 65, scale: MAJOR,    bpm: 110, leadWave: 'triangle', melody: [0,4,7,4, 2,5,7,5, 4,7,9,7, 4,2,0,-1, 0,4,7,9, 7,5,4,2, 0,2,4,5, 4,-1,0,-1], bass: [0,5,4,3] },
      8: { root: 56, scale: MINOR,    bpm: 150, leadWave: 'sawtooth', melody: [0,3,7,3, 5,7,8,7, 3,5,7,3, 0,-1,3,-1, 0,3,5,7, 8,10,8,7, 5,3,2,3, 0,-1,0,-1], bass: [0,3,7,5] },
      9: { root: 71, scale: PENTA,    bpm: 138, leadWave: 'square',   melody: [0,2,4,2, 3,4,2,0, 4,3,2,4, 0,-1,0,-1, 0,4,3,2, 4,3,2,0, 2,3,4,3, 0,-1,0,-1], bass: [0,2,3,4] },
      10:{ root: 55, scale: HARMINOR, bpm: 96,  leadWave: 'sawtooth', melody: [0,3,7,8, 7,5,3,2, 0,3,5,7, 8,7,5,3, 0,3,7,8, 11,10,8,7, 5,3,2,0, 0,-1,-1,-1], bass: [0,7,5,3] },
    };
    return R[world] ?? R[1];
  }
}

export const AudioManager = new AudioManagerImpl();
